import { Icon as BlueIcon } from '@blueprintjs/core';
import { TruncatedTextTooltip } from '@components/Tooltip';

import { MenuAlign, useMenuState } from '@szhsin/react-menu';
import { forwardRef, Ref, useImperativeHandle, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import { GlobalOverlay } from '@components';
import { cx } from '@utils/cx';
import {
  Close,
  Header,
  IconButton,
  IconContainer,
  IconWrapper,
  Item,
  ItemText,
  Menu,
  Title,
  Description,
  Warning,
  WarningBox,
  WarningMessage,
  WarningHeading,
  Separators,
  Wrapper,
} from './GlobalMenu.styles';

export interface IItemList {
  label: string;
  description?: string;
  iconName: any;
  iconColor?: string | undefined;
  onClick?: () => void;
  disabled?: boolean;
  stillOpenAfterClick?: boolean;
  toolTipText?: string;
  toolTipId?: string;
  toolTipEvent?: string;
  toolTipAfterShow?: () => void;
  disableToolTip?: boolean;
  hideAfterShow?: boolean;
  bottomSeparator?: boolean;
}

interface IGlobalMenu {
  itemList: Array<IItemList>;
  title?: string;
  align?: MenuAlign;
  onOpen?: () => void;
  onClose?: () => void;
  hoverColor?: string;
  width?: string;
  height?: string;
  paddingTop?: string;
  menuIcon?: any;
  warning?: string;
  warningMessage?: string;
  TopLabel?: () => JSX.Element;

  classNames?: { iconWrapper?: string; menuClose?: string };
}

export interface IRefGlobalMenu {
  isOpen: boolean;
  toggleMenu: (open?: boolean | undefined) => void;
}

const GlobalMenu = forwardRef(
  (
    {
      title,
      align,
      itemList,
      onOpen,
      onClose,
      height,
      hoverColor,
      paddingTop,
      width,
      menuIcon,
      warning,
      warningMessage,
      TopLabel,
      classNames,
    }: IGlobalMenu,
    refGlobalMenu: Ref<IRefGlobalMenu>,
  ) => {
    const ref = useRef(null);

    const { iconWrapper, menuClose } = classNames || {};

    const _iconWrapper = cx('GlobalMenu_IconWrapper', iconWrapper ?? '');
    const _menuClose = cx('GlobalMenu_Close', menuClose ?? '');

    const [menuProps, toggleMenu] = useMenuState({
      transition: false,
      unmountOnClose: true,
    });

    const isOpen = menuProps.state === 'open';

    useImperativeHandle(refGlobalMenu, () => ({ isOpen, toggleMenu }));

    return (
      <Wrapper onClick={e => e.stopPropagation()}>
        <IconButton
          ref={ref}
          className="btn"
          onClick={() => {
            toggleMenu(true);
            if (onOpen) {
              onOpen();
            }
          }}
        >
          <IconWrapper
            hoverColor={hoverColor}
            width={width}
            height={height}
            paddingTop={paddingTop}
            className={_iconWrapper}
          >
            <BlueIcon icon={menuIcon || 'more'} color="#8F95B2" />
          </IconWrapper>
        </IconButton>
        {isOpen && <GlobalOverlay />}

        <Menu
          {...menuProps}
          align={align || 'start'}
          anchorRef={ref}
          onClose={() => {
            toggleMenu(false);
            if (onClose) {
              onClose();
            }
          }}
        >
          <Header>
            <Title>{title || 'Options'}</Title>
            <Close className={_menuClose}>
              <BlueIcon icon="cross" color="#8F95B2" />
            </Close>
          </Header>

          {!!TopLabel && <TopLabel />}

          {warning && (
            <WarningBox>
              <Warning>
                <BlueIcon icon="error" color="#D14343" />
                <WarningHeading>{warning}</WarningHeading>
              </Warning>
              <WarningMessage>{warningMessage}</WarningMessage>
            </WarningBox>
          )}
          {itemList.map(item => (
            <div key={item.label}>
              <Item
                onClick={item.onClick}
                data-tip={item.toolTipText}
                data-for={item.toolTipId}
                data-event={item.toolTipEvent}
                disabled={item.disabled}
                data-testid="global-menu-item"
              >
                <IconContainer>
                  <BlueIcon
                    icon={item.iconName}
                    color={
                      item.iconColor || item.disabled ? '#C1C4D6' : '#8F95B2'
                    }
                  />
                  <ItemText disabled={item.disabled}>{item.label}</ItemText>
                </IconContainer>

                {item.description && (
                  <Description
                    disabled={item.disabled}
                    description={item.description}
                  >
                    {item.description}
                  </Description>
                )}
              </Item>
              {item.bottomSeparator && <Separators />}
              <TruncatedTextTooltip
                id={item.toolTipId}
                disable={item.disableToolTip}
                globalEventOff={item.toolTipEvent}
                afterShow={() => {
                  if (item.onClick && item.toolTipEvent === 'click') {
                    item.onClick();
                  }
                  item.toolTipAfterShow;
                  item.hideAfterShow ? setTimeout(ReactTooltip.hide, 600) : '';
                }}
              />
            </div>
          ))}
        </Menu>
      </Wrapper>
    );
  },
);

export { GlobalMenu };
